import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmt, fmtCents } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Sparkles, Check, TrendingDown, ShieldCheck, Zap, Mountain } from "lucide-react";

const TIER_META = {
  quick_win: { title: "Quick Wins", icon: Zap, color: "bg-emerald-100 text-emerald-700", desc: "Fast, low-effort savings." },
  easy_change: { title: "Easy Changes", icon: ShieldCheck, color: "bg-sky-100 text-sky-700", desc: "Small habit changes with real impact." },
  bigger_opportunity: { title: "Bigger Opportunities", icon: Mountain, color: "bg-amber-100 text-amber-700", desc: "Bigger commitments, bigger savings." },
};

const CONF_LABEL = { high: "High confidence", medium: "Medium confidence", low: "Low confidence" };

export default function FindMoney() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [running, setRunning] = useState(false);
  const [addedIds, setAdded] = useState({}); // key -> action id
  const [progressSummary, setProgress] = useState(null);

  const load = async () => {
    setRunning(true);
    try {
      const [{ data: findRes }, { data: sav }] = await Promise.all([
        api.post("/find_money"),
        api.get("/savings_actions"),
      ]);
      setData(findRes);
      setProgress(sav);
      const map = {};
      sav.items.forEach((a) => { if (a.status === "active") map[`${a.category}-${a.title}`] = a.id; });
      setAdded(map);
    } catch (e) {
      toast.error("Analysis failed");
    }
    setRunning(false);
  };

  useEffect(() => { load(); }, []);

  const addToPlan = async (opp) => {
    try {
      const { data: created } = await api.post("/savings_actions", {
        title: opp.title,
        category: opp.category,
        current_monthly: opp.current_monthly,
        target_monthly: opp.target_monthly,
        estimated_monthly_savings: opp.estimated_monthly_savings,
        explanation: opp.explanation,
        confidence: opp.confidence,
        tier: opp.tier,
      });
      setAdded((m) => ({ ...m, [`${opp.category}-${opp.title}`]: created.id }));
      toast.success("Added to your plan");
      const { data: sav } = await api.get("/savings_actions");
      setProgress(sav);
    } catch { toast.error("Could not add to plan"); }
  };

  const markProgress = async (actionId, amount) => {
    try {
      await api.patch(`/savings_actions/${actionId}/progress`, { achieved_monthly: amount });
      const { data: sav } = await api.get("/savings_actions");
      setProgress(sav);
      toast.success("Progress updated");
    } catch { toast.error("Could not update"); }
  };

  return (
    <div className="pb-6">
      {/* Header */}
      <div className="px-6 pt-8 pb-4 flex items-center gap-3">
        <button onClick={() => nav(-1)} className="w-10 h-10 rounded-full border border-slate-200 flex items-center justify-center hover:bg-slate-50 transition-colors" aria-label="Back">
          <ArrowLeft size={18} strokeWidth={1.75} />
        </button>
        <div className="text-sm text-slate-500 font-medium">AI Money Analysis</div>
      </div>

      {/* Hero */}
      <div className="mx-6 rounded-3xl bg-slate-900 text-white p-6 relative overflow-hidden">
        <div className="absolute -right-10 -top-10 w-48 h-48 rounded-full bg-emerald-500/20 blur-3xl" />
        <div className="relative">
          <div className="flex items-center gap-2 text-emerald-300 text-xs uppercase tracking-widest font-semibold"><Sparkles size={14} /> WE FOUND MONEY</div>
          <div className="mt-2 font-manrope text-5xl sm:text-6xl font-extrabold tracking-tighter tabular-nums">
            {data ? fmt(data.total_potential_monthly) : "…"}
            <span className="text-slate-400 text-lg font-semibold">/mo</span>
          </div>
          <p className="mt-3 text-slate-300 text-sm leading-relaxed">
            {data && data.total_potential_monthly > 0
              ? <>Based on your recent spending, we identified approximately <b className="text-white">{fmt(data.total_potential_monthly)}/month</b> in potential savings — around <b className="text-white">{fmt(data.total_potential_annual)}</b> a year.</>
              : "We didn't find meaningful savings opportunities yet. Import more transactions or check back next month."}
          </p>
        </div>
      </div>

      {/* Progress */}
      {progressSummary && progressSummary.identified_monthly > 0 && (
        <div className="mx-6 mt-6 rounded-2xl border border-slate-200 p-5">
          <div className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Your savings plan</div>
          <div className="mt-3 grid grid-cols-3 gap-3">
            <MiniStat label="Identified" value={progressSummary.identified_monthly} />
            <MiniStat label="Achieved" value={progressSummary.achieved_monthly} strong />
            <MiniStat label="Remaining" value={progressSummary.remaining_monthly} />
          </div>
          <div className="mt-4 h-2 rounded-full bg-slate-100 overflow-hidden">
            <div className="h-full rounded-full bg-emerald-500 transition-all duration-500"
                 style={{ width: `${Math.min(100, (progressSummary.achieved_monthly / Math.max(1, progressSummary.identified_monthly)) * 100)}%` }} />
          </div>
        </div>
      )}

      {/* Groups */}
      {data && ["quick_win", "easy_change", "bigger_opportunity"].map((tier) => {
        const items = data.grouped[tier] || [];
        if (!items.length) return null;
        const meta = TIER_META[tier];
        const Icon = meta.icon;
        return (
          <div key={tier} className="mt-8 px-6">
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-xl ${meta.color} flex items-center justify-center`}><Icon size={16} strokeWidth={2} /></div>
              <div>
                <div className="font-manrope font-bold text-slate-900">{meta.title}</div>
                <div className="text-xs text-slate-500">{meta.desc}</div>
              </div>
            </div>
            <div className="mt-4 space-y-4">
              {items.map((o, idx) => {
                const key = `${o.category}-${o.title}`;
                const addedId = addedIds[key];
                return (
                  <div key={idx} className="rounded-2xl border border-slate-200 p-5" data-testid={`opportunity-${tier}-${idx}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-xs uppercase tracking-widest text-slate-400 font-semibold">{o.category}</div>
                        <div className="mt-1 font-manrope text-lg font-bold text-slate-900 tracking-tight">{o.title}</div>
                      </div>
                      <span className="rounded-full px-2 py-1 text-[10px] font-bold uppercase tracking-wider bg-slate-100 text-slate-600">{CONF_LABEL[o.confidence] || o.confidence}</span>
                    </div>

                    <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
                      <Kv label="Current" value={fmtCents(o.current_monthly) + "/mo"} />
                      {o.avg_6mo > 0 && <Kv label="6-mo average" value={fmtCents(o.avg_6mo) + "/mo"} />}
                      <Kv label="Target" value={fmtCents(o.target_monthly) + "/mo"} />
                      <Kv label="Est. savings" value={<span className="text-emerald-600 font-bold">{fmt(o.estimated_monthly_savings)}/mo</span>} />
                      <Kv label="Annual" value={<span className="text-emerald-600 font-bold">{fmt(o.estimated_annual_savings)}</span>} />
                    </div>

                    <div className="mt-4 bg-slate-50 rounded-xl p-4 text-sm text-slate-700 leading-relaxed">
                      <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-1">Why?</div>
                      {o.explanation}
                    </div>

                    {addedId ? (
                      <ProgressRow actionId={addedId} target={o.estimated_monthly_savings} currentAchieved={progressSummary?.items?.find((a) => a.id === addedId)?.achieved_monthly || 0} onSave={markProgress} />
                    ) : (
                      <button
                        data-testid={`add-to-plan-btn-${tier}-${idx}`}
                        onClick={() => addToPlan(o)}
                        className="mt-4 w-full bg-slate-900 text-white rounded-full py-3 font-semibold hover:bg-slate-800 active:scale-[0.98] transition-transform duration-150"
                      >
                        ADD TO MY PLAN
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {data && data.opportunities.length === 0 && (
        <div className="mx-6 mt-10 text-center text-slate-500">
          <p>Nothing to flag right now.</p>
          <p className="mt-1 text-sm">Add more transactions or run analysis again next month.</p>
        </div>
      )}

      <div className="mx-6 mt-10 mb-6">
        <button onClick={load} disabled={running} data-testid="rerun-btn" className="w-full bg-slate-100 text-slate-900 rounded-full py-3 font-semibold hover:bg-slate-200 transition-colors duration-200 disabled:opacity-60">
          {running ? "Analyzing…" : "Re-run analysis"}
        </button>
        <p className="mt-4 text-[11px] text-slate-400 text-center leading-relaxed">These are estimates based on your transaction history — not guarantees. Actual savings depend on your choices.</p>
      </div>
    </div>
  );
}

function Kv({ label, value }) {
  return (
    <div className="flex flex-col">
      <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{label}</span>
      <span className="font-manrope font-semibold text-slate-900 tabular-nums">{value}</span>
    </div>
  );
}
function MiniStat({ label, value, strong }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{label}</div>
      <div className={`font-manrope font-extrabold tracking-tight tabular-nums ${strong ? "text-emerald-600 text-xl" : "text-slate-900 text-lg"}`}>{fmt(value)}</div>
    </div>
  );
}

function ProgressRow({ actionId, target, currentAchieved, onSave }) {
  const [v, setV] = useState(currentAchieved || 0);
  return (
    <div className="mt-4 rounded-xl bg-emerald-50 border border-emerald-200 p-4">
      <div className="flex items-center gap-2 text-emerald-700 text-xs font-semibold uppercase tracking-widest"><Check size={14} /> In your plan</div>
      <div className="mt-3 text-xs text-slate-600">How much have you actually saved so far this month?</div>
      <div className="mt-2 flex items-center gap-2">
        <input type="number" min="0" max={target * 2} value={v} onChange={(e) => setV(Number(e.target.value))} className="flex-1 bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm" />
        <button onClick={() => onSave(actionId, v)} className="bg-slate-900 text-white rounded-full px-4 py-2 text-sm font-semibold hover:bg-slate-800 transition-colors duration-200">Save</button>
      </div>
    </div>
  );
}
